import { useEffect, useMemo, useState } from "react";
import api from "api/axiosInstance";
import styles from "../AdminPanel.module.scss";

type EmployeeRow = {
  id: number;
  surname: string;
  name: string;
  patronymic?: string;
};

type GalleryRow = {
  id: number;
  title: string;
  image_path: string;
  created_at?: string;
  category_id: number;
};

type CategoryRow = {
  id: number;
  title: string;
};

type EmployeeWorkRow = {
  id: number;
  employee_id: number;
  title: string;
  problem: string;
  solution: string;
  photo_ids: number[];
  sort_order: number;
  created_at?: string;
  updated_at?: string;
};

const CATEGORY_TITLE_MAX = 30;

function fullName(e?: EmployeeRow | null) {
  if (!e) return "—";
  return [e.surname, e.name, e.patronymic].filter(Boolean).join(" ").trim();
}

function compactEmployeeName(e?: EmployeeRow | null) {
  if (!e) return "Работы";

  const surname = (e.surname || "").trim();
  const name = (e.name || "").trim();
  const patronymic = (e.patronymic || "").trim();

  const parts = [
    surname,
    name ? `${name[0]}.` : "",
    patronymic ? `${patronymic[0]}.` : "",
  ].filter(Boolean);

  return parts.join(" ").trim() || fullName(e) || "Работы";
}

function trimCategoryTitle(raw: string, max = CATEGORY_TITLE_MAX) {
  const clean = raw.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return clean.slice(0, max).trim();
}

function workCategoryTitle(e?: EmployeeRow | null) {
  const longBase =
    fullName(e) && fullName(e) !== "—" ? `${fullName(e)} работы` : "Работы";

  const normalizedLong = longBase.replace(/\s+/g, " ").trim();
  if (normalizedLong.length <= CATEGORY_TITLE_MAX) {
    return normalizedLong;
  }

  const compactBase = `${compactEmployeeName(e)} работы`.replace(/\s+/g, " ").trim();
  return trimCategoryTitle(compactBase);
}

function safeImgSrc(path?: string) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/")) return path;
  return `/${path}`;
}

function extractErr(e: any) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  const msg =
    typeof data === "string"
      ? data
      : data?.message
      ? Array.isArray(data.message)
        ? data.message.join(", ")
        : String(data.message)
      : data?.error
      ? String(data.error)
      : e?.message;

  return status ? `${msg} (HTTP ${status})` : msg || "Неизвестная ошибка";
}

async function uploadGalleryImage(file: File, title: string, categoryId: number) {
  const fd = new FormData();
  fd.append("image_path", file);
  fd.append("title", title);
  fd.append("category_id", String(categoryId));

  const res = await api.post<GalleryRow>("/gallery/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return res.data;
}

function fileKey(file: File) {
  return `${file.name}_${file.size}_${file.lastModified}`;
}

export default function EmployeeWorksManager() {
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [gallery, setGallery] = useState<GalleryRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [works, setWorks] = useState<EmployeeWorkRow[]>([]);

  const galleryMap = useMemo(() => {
    const m = new Map<number, GalleryRow>();
    gallery.forEach((g) => m.set(g.id, g));
    return m;
  }, [gallery]);

  const employeeMap = useMemo(() => {
    const m = new Map<number, EmployeeRow>();
    employees.forEach((e) => m.set(e.id, e));
    return m;
  }, [employees]);

  const categoryMap = useMemo(() => {
    const m = new Map<number, CategoryRow>();
    categories.forEach((c) => m.set(c.id, c));
    return m;
  }, [categories]);

  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const [editId, setEditId] = useState<number | null>(null);

  const [employeeId, setEmployeeId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [problem, setProblem] = useState("");
  const [solution, setSolution] = useState("");
  const [sortOrder, setSortOrder] = useState<string>("0");

  const [existingPhotoIds, setExistingPhotoIds] = useState<number[]>([]);
  const [files, setFiles] = useState<File[]>([]);

  const selectedEmployee = useMemo(() => {
    const parsed = Number(employeeId);
    return Number.isFinite(parsed) ? employeeMap.get(parsed) ?? null : null;
  }, [employeeId, employeeMap]);

  const resetForm = () => {
    setEditId(null);
    setEmployeeId("");
    setTitle("");
    setProblem("");
    setSolution("");
    setSortOrder("0");
    setExistingPhotoIds([]);
    setFiles([]);
  };

  const appendFiles = (nextFiles: File[]) => {
    if (!nextFiles.length) return;

    setFiles((prev) => {
      const existing = new Set(prev.map((f) => fileKey(f)));
      const uniqueToAdd = nextFiles.filter((f) => !existing.has(fileKey(f)));
      return [...prev, ...uniqueToAdd];
    });
  };

  const removePendingFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const loadAll = async () => {
    setLoading(true);
    setErrorText(null);

    try {
      const [empRes, galleryRes, categoryRes, worksRes] = await Promise.all([
        api.get<EmployeeRow[]>("/employee"),
        api.get<GalleryRow[]>("/gallery"),
        api.get<CategoryRow[]>("/category"),
        api.get<EmployeeWorkRow[]>("/employee-work"),
      ]);

      setEmployees(Array.isArray(empRes.data) ? empRes.data : []);
      setGallery(Array.isArray(galleryRes.data) ? galleryRes.data : []);
      setCategories(Array.isArray(categoryRes.data) ? categoryRes.data : []);
      setWorks(Array.isArray(worksRes.data) ? worksRes.data : []);
    } catch (e: any) {
      setErrorText(`Ошибка загрузки: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureEmployeeWorkCategory = async (employee: EmployeeRow) => {
    const normalizedTitle = workCategoryTitle(employee);

    const existing = categories.find((c) => c.title.trim() === normalizedTitle.trim());
    if (existing) return existing.id;

    const created = await api.post<CategoryRow>("/category", { title: normalizedTitle });
    return created.data.id;
  };

  const handleEdit = (row: EmployeeWorkRow) => {
    setEditId(row.id);
    setEmployeeId(String(row.employee_id));
    setTitle(row.title ?? "");
    setProblem(row.problem ?? "");
    setSolution(row.solution ?? "");
    setSortOrder(String(row.sort_order ?? 0));
    setExistingPhotoIds(Array.isArray(row.photo_ids) ? row.photo_ids : []);
    setFiles([]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Удалить кейс? Фото из галереи удалены не будут.")) return;

    setLoading(true);
    setErrorText(null);

    try {
      await api.delete(`/employee-work/${id}`);

      if (editId === id) {
        resetForm();
      }

      await loadAll();
    } catch (e: any) {
      setErrorText(`Ошибка удаления: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const removeExistingPhoto = (id: number) => {
    setExistingPhotoIds((prev) => prev.filter((x) => x !== id));
  };

  const handleSave = async () => {
    setLoading(true);
    setErrorText(null);

    try {
      const parsedEmployeeId = Number(employeeId);
      const parsedSortOrder = Number(sortOrder || "0");

      const cleanTitle = title.trim();
      const cleanProblem = problem.trim();
      const cleanSolution = solution.trim();

      if (!Number.isFinite(parsedEmployeeId) || parsedEmployeeId <= 0) {
        setErrorText("Выберите сотрудника.");
        setLoading(false);
        return;
      }

      const employee = employeeMap.get(parsedEmployeeId);
      if (!employee) {
        setErrorText("Не удалось определить сотрудника.");
        setLoading(false);
        return;
      }

      if (!cleanTitle) {
        setErrorText("Укажите заголовок кейса.");
        setLoading(false);
        return;
      }

      if (!cleanProblem) {
        setErrorText("Заполните поле «Проблема».");
        setLoading(false);
        return;
      }

      if (!cleanSolution) {
        setErrorText("Заполните поле «Решение».");
        setLoading(false);
        return;
      }

      let uploadedPhotoIds: number[] = [];

      if (files.length > 0) {
        let categoryId: number;

        try {
          categoryId = await ensureEmployeeWorkCategory(employee);
        } catch (e: any) {
          setErrorText(`Ошибка создания/поиска категории для фото: ${extractErr(e)}`);
          setLoading(false);
          return;
        }

        if (!Number.isFinite(categoryId) || categoryId <= 0) {
          setErrorText("Не удалось получить корректный ID категории для фото.");
          setLoading(false);
          return;
        }

        try {
          const uploaded = await Promise.all(
            files.map((file, index) =>
              uploadGalleryImage(file, `${cleanTitle} — фото ${index + 1}`, categoryId)
            )
          );

          uploadedPhotoIds = uploaded
            .map((x) => x?.id)
            .filter((x): x is number => typeof x === "number");
        } catch (e: any) {
          setErrorText(`Ошибка загрузки фото в галерею: ${extractErr(e)}`);
          setLoading(false);
          return;
        }
      }

      const finalPhotoIds = [...existingPhotoIds, ...uploadedPhotoIds];

      if (finalPhotoIds.length === 0) {
        setErrorText("Добавьте хотя бы одно фото кейса.");
        setLoading(false);
        return;
      }

      const payload = {
        employee_id: parsedEmployeeId,
        title: cleanTitle,
        problem: cleanProblem,
        solution: cleanSolution,
        photo_ids: finalPhotoIds,
        sort_order: Number.isFinite(parsedSortOrder) ? parsedSortOrder : 0,
      };

      try {
        if (editId) {
          await api.patch(`/employee-work/${editId}`, payload);
        } else {
          await api.post("/employee-work", payload);
        }
      } catch (e: any) {
        setErrorText(`Ошибка сохранения кейса: ${extractErr(e)}`);
        setLoading(false);
        return;
      }

      resetForm();
      await loadAll();
    } catch (e: any) {
      setErrorText(`Ошибка сохранения: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const sortedWorks = useMemo(() => {
    return [...works].sort((a, b) => {
      const byEmployee = (a.employee_id ?? 0) - (b.employee_id ?? 0);
      if (byEmployee !== 0) return byEmployee;

      const byOrder = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (byOrder !== 0) return byOrder;

      return (b.id ?? 0) - (a.id ?? 0);
    });
  }, [works]);

  return (
    <>
      <div className={styles.form_block}>
        <h3>{editId ? "Редактировать выполненную работу" : "Добавить выполненную работу"}</h3>

        {errorText ? <div style={{ color: "#c00", marginBottom: 12 }}>{errorText}</div> : null}

        <label>Сотрудник</label>
        <select
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          disabled={loading}
        >
          <option value="">Выберите сотрудника</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>
              {fullName(e)}
            </option>
          ))}
        </select>

        {selectedEmployee ? (
          <div style={{ marginTop: -6, marginBottom: 12, fontSize: 13, opacity: 0.75 }}>
            Новые фото будут сложены в категорию: <b>{workCategoryTitle(selectedEmployee)}</b>
          </div>
        ) : null}

        <label>Заголовок кейса</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Например: Эстетическая реставрация фронтального зуба"
          disabled={loading}
        />

        <div
          style={{
            marginTop: 8,
            marginBottom: 8,
            fontSize: 16,
            fontWeight: 700,
            color: "#333",
          }}
        >
          Проблема
        </div>

        <textarea
          value={problem}
          onChange={(e) => setProblem(e.target.value)}
          rows={5}
          style={{ width: "60%" }}
          placeholder="Опишите исходную ситуацию пациента"
          disabled={loading}
        />

        <div
          style={{
            marginTop: 8,
            marginBottom: 8,
            fontSize: 16,
            fontWeight: 700,
            color: "#333",
          }}
        >
          Решение
        </div>

        <textarea
          value={solution}
          onChange={(e) => setSolution(e.target.value)}
          rows={5}
          style={{ width: "60%" }}
          placeholder="Опишите, что было сделано и какой результат получен"
          disabled={loading}
        />

        <label>Порядок сортировки</label>
        <input
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          placeholder="0"
          disabled={loading}
        />

        <hr style={{ margin: "10px 0 16px" }} />

        <h3 style={{ marginTop: 0 }}>Фотографии кейса</h3>

        {existingPhotoIds.length > 0 ? (
          <div className={styles.employeeWorksPhotoGrid}>
            {existingPhotoIds.map((photoId) => {
              const photo = galleryMap.get(photoId);
              const src = safeImgSrc(photo?.image_path);
              const category = photo?.category_id ? categoryMap.get(photo.category_id) : null;

              return (
                <div key={photoId} className={styles.employeeWorksPhotoCard}>
                  {src ? (
                    <img
                      src={src}
                      alt={photo?.title || `Фото ${photoId}`}
                      className={styles.employeeWorksPhotoImg}
                    />
                  ) : (
                    <div className={styles.employeeWorksPhotoFallback}>Нет превью</div>
                  )}

                  <div className={styles.employeeWorksPhotoMeta}>
                    <div className={styles.employeeWorksPhotoTitle}>
                      {photo?.title || `ID: ${photoId}`}
                    </div>

                    {category?.title ? (
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        Категория: {category.title}
                      </div>
                    ) : null}

                    <button
                      type="button"
                      className={styles.btn_danger}
                      onClick={() => removeExistingPhoto(photoId)}
                      disabled={loading}
                    >
                      Убрать из кейса
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ opacity: 0.75, marginBottom: 12 }}>
            Пока фото не выбраны.
          </div>
        )}

        <label>Добавить новые фото</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            appendFiles(picked);
            e.currentTarget.value = "";
          }}
          disabled={loading}
        />

        <div style={{ marginTop: 8, opacity: 0.8, fontSize: 13 }}>
          Выбрано файлов: {files.length}
        </div>

        {files.length > 0 ? (
          <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
            {files.map((file, idx) => (
              <div
                key={`${fileKey(file)}_${idx}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "10px 12px",
                  border: "1px solid #e3e3e3",
                  borderRadius: 8,
                  background: "#fff",
                  maxWidth: 560,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      wordBreak: "break-word",
                    }}
                  >
                    {file.name}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.btn_danger}
                  onClick={() => removePendingFile(idx)}
                  disabled={loading}
                >
                  Убрать
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button className={styles.btn_primary} onClick={handleSave} disabled={loading}>
            {loading ? "Сохраняю..." : editId ? "Сохранить" : "Создать"}
          </button>

          {editId ? (
            <button
              className={styles.btn}
              onClick={resetForm}
              disabled={loading}
              type="button"
            >
              Отмена
            </button>
          ) : null}
        </div>
      </div>

      <div className={styles.table_wrapper}>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Сотрудник</th>
              <th>Заголовок</th>
              <th>Порядок</th>
              <th>Фото</th>
              <th>Действия</th>
            </tr>
          </thead>

          <tbody>
            {sortedWorks.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>{fullName(employeeMap.get(row.employee_id))}</td>
                <td>{row.title || "—"}</td>
                <td>{row.sort_order ?? 0}</td>
                <td>{Array.isArray(row.photo_ids) ? row.photo_ids.length : 0}</td>
                <td>
                  <div className={styles.actions}>
                    <button
                      className={styles.btn_primary}
                      onClick={() => handleEdit(row)}
                      disabled={loading}
                    >
                      Редактировать
                    </button>
                    <button
                      className={styles.btn_danger}
                      onClick={() => handleDelete(row.id)}
                      disabled={loading}
                    >
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {sortedWorks.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ opacity: 0.7 }}>
                  Пока нет кейсов
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}
